import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import Shimmer from "../AdminManagement/components/ViewAdminDetailsShimmer";
import useApi from "../../services/useApi";
import useTranslation from "../../components/CustomHooks/useTranslation";
import { useNavigate, useParams } from "react-router-dom";
import { getFirefoxCssFix } from "../../components/commonfunctions/getFirefoxCssFix";
import { adminContents } from "../AdminManagement/components/AdminManagementContent";
import moment from "moment";
import CustomModal from "../../components/CustomModal";
import TextArea from "../../components/inputfields/TextArea";
import { useDispatch } from "react-redux";
import { showToast } from "../../components/toastMessage/toastSlice";
import Button from "../../components/buttons/Button";
import CurrencyInputField from "../../components/inputfields/InputFieldWithCurrency";

type TransactionDetails = {
  id: string;
  transaction_id: string;
  transaction_date: string;
  requested_date: string;
  transaction_category: string;
  transaction_category_status: string;
  wallet_type: string;
  requested_by: string;
  requested_from: string;
  payment_type: string;
  amount: string;
  e_topup_stock_transferred: string | null;
  receiver_payment_source: string;
  sender_payment_source: string;
  sender_source_name: string;
  transaction_receipt: string;
  transaction_status: string;
  note: string;
  sender_location: string;
  receiver_location: string;
  phone_number: string | null;
  telecom_name: string | null;
  flagged_reason: string | null;
  rejection_reason: string | null;
  is_flagged: boolean;
  flag_status: string;
  flagged_at?: string;
  sender?: string;
  sender_phone_number?: string;
  sender_email?: string;
  recipient?: string;
  recipient_phone_number?: string;
  recipient_email?: string;
  recipient_location?: string;
  commission_details?: {
    commission_amount?: string;
    commission_percentage?: string;
    commission_type?: string;
  } | null;
};

// Helper to render a label-value pair
const renderField = (label: string, value: any) => (
  <div>
    <h1 className="text-body2 text-primary_text">{label}</h1>
    <p className="text-body1 break-all">{value ?? "-"}</p>
  </div>
);

export default function ViewReversalRequest() {
  const { getAPI, PatchAPI } = useApi();
  const { translate, notEnglish } = useTranslation(adminContents);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const [transactionDetails, setTransactionDetails] = useState<TransactionDetails | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApprovalModal, setIsApprovalModal] = useState(false);
  const [reasonInput, setReasonInput] = useState<string>('');
  const [reasonInputError, setReasonInputError] = useState<string>('');
  const [isFlagged, setIsFlagged] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [flagStatus, setFlagStatus] = useState<string>('');
  const [textError, setTextError] = useState<string>('');
  const dispatch = useDispatch();
  const [isFlagDisabledByTime, setIsFlagDisabledByTime] = useState(false);
  const [calculatedCommissionAmount, setCalculatedCommissionAmount] = useState<number | null>(null);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await getAPI(`transactions/reversal-requests/${id}`);
      if (!response?.error) {
        setTransactionDetails(response?.data?.data);
        setIsFlagged(response?.data?.data?.flagged);
        if (response?.data?.data?.flagged && response?.data?.data?.flag_status === "Approved") {
          setFlagStatus("approved");
        } else if (response?.data?.data?.flagged && response?.data?.data?.flag_status === "Pending") {
          setFlagStatus('pending');
        } else if (response?.data?.data?.flagged && response?.data?.data?.flag_status === "Rejected") {
          setFlagStatus('rejected');
        } else {
          setFlagStatus('');
        }
      } else {
        navigate("/not-found", {
          state: { route: "/admin-management/list-of-admins" },
        });
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
    }
    setLoading(false);
  };

  useLayoutEffect(() => {
    fetchDetails();
  }, []);

  const calculateCommissionAmount = useCallback((reversalAmount: string) => {
    if (!transactionDetails?.commission_details) {
      setCalculatedCommissionAmount(null);
      return;
    }

    const { commission_percentage } = transactionDetails.commission_details;
    
    if (!commission_percentage || Number(commission_percentage) <= 0.09) {
      setCalculatedCommissionAmount(null);
      return;
    }

    const amount = Number(reversalAmount);
    if (isNaN(amount) || amount <= 0) {
      setCalculatedCommissionAmount(null);
      return;
    }

    const commissionAmount = (amount * Number(commission_percentage)) / 100;
    setCalculatedCommissionAmount(commissionAmount);
  }, [transactionDetails]);

  useEffect(() => {
    if (!transactionDetails) return;
    const transactionDate = moment(transactionDetails.transaction_date);
    const checkDisabled = () => {
      const now = moment();
      const diffMinutes = now.diff(transactionDate, 'minutes');
      if (diffMinutes >= 2880) {
        setIsFlagDisabledByTime(true);
        return true;
      } else {
        setIsFlagDisabledByTime(false);
        return false;
      }
    };
    // Initial check
    if (checkDisabled()) return;
    // Poll every 10 seconds
    const interval = setInterval(() => {
      if (checkDisabled()) {
        clearInterval(interval);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [transactionDetails]);

  useEffect(() => {
    if (isModalOpen && isApprovalModal && transactionDetails) {
      const defaultAmount = transactionDetails.transaction_category === 'Recharge' 
        ? transactionDetails.amount.toString() 
        : (transactionDetails.e_topup_stock_transferred || transactionDetails.amount).toString();
      setReasonInput(defaultAmount);
      calculateCommissionAmount(defaultAmount);
    } else {
      setCalculatedCommissionAmount(null);
    }
  }, [isModalOpen, isApprovalModal, transactionDetails, calculateCommissionAmount]);

  const handleApproval = async () => {
    setReasonInputError('');
    if (!reasonInput.trim()) {
      setReasonInputError('This field is required');
      return;
    }
    if (Number(reasonInput) === 0) {
      setReasonInputError("Amount must be greater than AFA 0.00");
      return;
    }
    const maxAmount = transactionDetails?.transaction_category === 'Recharge' 
      ? transactionDetails?.amount 
      : (transactionDetails?.e_topup_stock_transferred || transactionDetails?.amount);
    if (Number(reasonInput) > Number(maxAmount)) {
      setReasonInputError(`Amount cannot be greater than AFA ${maxAmount}`);
      return;
    }
    setButtonLoading(true);
    const payload = { flag_status: isApprovalModal ? "Approved" : "Rejected", ...(isApprovalModal ? { amount: Number(reasonInput.trim()) } : { rejection_reason: reasonInput.trim() }) };
    try {
      const response = await PatchAPI(`transactions/reversal-requests/${id}`, payload);
      if (!response?.error) {
        setIsFlagged(!isFlagged);
        setIsModalOpen(false);
        setReasonInput('');
        setReasonInputError('');
        setCalculatedCommissionAmount(null);
        dispatch(showToast({
          type: 'success',
          message: isApprovalModal ? 'Reversal request approved successfully' : 'Reversal request rejected successfully',
        }));
        fetchDetails();
      } else {
        dispatch(showToast({
          type: 'error',
          message: response?.data?.response?.data?.message || 'Something went wrong',
        }));
      }
    } catch (error) {
      dispatch(showToast({
        type: 'error',
        message: 'Something went wrong',
      }));
    } finally {
      setButtonLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setReasonInput('');
    setReasonInputError('');
    setCalculatedCommissionAmount(null);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^[0-9].*$/.test(value)) {
      setReasonInput(value);
      setReasonInputError('');
      calculateCommissionAmount(value);
    }
  };

  const getFlaggingAllowed = () => {
    if (!transactionDetails) return false;
    if (transactionDetails.transaction_category_status !== "Approved") return false;
    if (isFlagged) return true;
    if (isFlagDisabledByTime) return false;
    const transactionDate = moment(transactionDetails.transaction_date);
    const now = moment();
    return now.diff(transactionDate, 'minutes') < 2880;
  };

  const getProfileTextColor = (status: any) => {
    if (status === "Approved") {
      return "text-[#22B14C] bg-positive_light";
    } else if (status === "In Review") {
      return "text-[#0072E4] bg-[#E5F0FC]";
    } else if (status === "Pending" || status === "In Progress") {
      return "text-[#FD9101] bg-[#FFEFD9]";
    } else if (status === "Rejected") {
      return "text-[#FF0000] bg-[#FFE5E5]";
    } else {
      return "";
    }
  }

  return (
    <div dir={notEnglish ? "rtl" : "ltr"}>
      <Breadcrumbs
        navPath={[
          "Reversal Request",
          "List of Reversal Request Transactions",
          "View Reversal Request",
        ]}
        links={["/reversal-requests", "/reversal-requests"]}
        heading={`View Reversal Request`}
      />
      {!loading && transactionDetails ? (
        <div className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col">
          <div className="px-6 py-5 border-b border-[#E5E9EB] flex flex-wrap gap-3 justify-between items-center">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <span className="text-title3 whitespace-nowrap">Transaction ID - </span>
              <span
                title={"Transaction"}
                data-testid="transaction-id"
                className="text-heading3 break-all"
              >
                {transactionDetails.transaction_id || "-"}
              </span>
              <span
                className={`${getProfileTextColor(
                  transactionDetails?.flag_status
                )} py-0.5 px-2 text-body1 rounded-md whitespace-nowrap`}
              >
                {transactionDetails?.flag_status}
              </span>
            </div>
            {transactionDetails.flag_status === 'Pending' && <div className="flex items-center gap-3 flex-shrink-0">
              <Button
                buttonName={translate("Approve")}
                buttonOnclick={() => {
                  setIsApprovalModal(true);
                  setIsModalOpen(true);
                }}
                className={"bg-positive w-[90px]"}
                buttonHight="h-[40px]"
                bgColor={"bg-positive"}
                textSize="text-large"
                testId="status-btn"
              />
              <Button
                buttonName={translate("Reject")}
                className="py-2 h-fit px-4 w-[90px]"
                buttonHight="h-[40px]"
                textSize="text-large"
                buttonOnclick={() => {
                  setIsApprovalModal(false);
                  setIsModalOpen(true);
                }}
                testId="update-btn"
                buttonDisabled={false}
                bgColor={"bg-negative"}
              />
            </div>}
          </div>
          <div
            className={`flex-grow px-6 pb-4 my-4 h-viewAdminHeight ${getFirefoxCssFix(
              "scroll"
            )} overflow-y-scroll`}
          >
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Dynamic field rendering based on transaction type */}
              {(() => {
                if (!transactionDetails) return null;
                // Common fields
                const commonFields = [
                  {
                    label: "Transaction Date & Time",
                    value: moment(transactionDetails.transaction_date).format("DD-MM-YYYY, hh:mm A") || "-",
                  },
                  {
                    label: "Reversal Request Raised Date & Time",
                    value: transactionDetails.flagged_at
                      ? moment(transactionDetails.flagged_at).format("DD-MM-YYYY, hh:mm A")
                      : "-",
                  },
                  {
                    label: "Transaction Type (Status)",
                    value: transactionDetails.transaction_category
                      ? `${transactionDetails.transaction_category} (${transactionDetails.transaction_category_status})`
                      : "-",
                  },
                ];
                // eTop Up fields
                const eTopUpFields = [
                  { label: "Sender Name (User Code)", key: "sender" },
                  { label: "Sender's Phone Number", key: "sender_phone_number" },
                  { label: "Sender's Email Address", key: "sender_email" },
                  { label: "Recipient Name (User Code)", key: "recipient" },
                  { label: "Recipient's Phone Number", key: "recipient_phone_number" },
                  { label: "Recipient's Email Address", key: "recipient_email" },
                  { label: "Sender's Location", key: "sender_location" },
                  { label: "Recipient's Location", key: "recipient_location" },
                  { label: "Wallet", key: "wallet_type" },
                  { label: "Amount", value: transactionDetails.amount ? `AFA ${transactionDetails.amount}` : "-" },
                  { label: "Sent Amount", value: transactionDetails.e_topup_stock_transferred ? `AFA ${transactionDetails.e_topup_stock_transferred}` : "-" },
                  { label: "Payment Type", value: transactionDetails.payment_type ? transactionDetails.payment_type.charAt(0).toUpperCase() + transactionDetails.payment_type.slice(1) : "-" },
                ];
                // Other transaction fields
                const otherFields = [
                  { label: "Recharged Phone Number", key: "phone_number" },
                  { label: "Telecom", key: "telecom_name" },
                  { label: "Wallet", key: "wallet_type" },
                  { label: "Initiator's Name (User Code)", key: "recipient" },
                  { label: "Initiator's Phone Number", key: "recipient_phone_number" },
                  { label: "Initiator's Email Address", key: "recipient_email" },
                  { label: "Initiator's Location", key: "recipient_location" },
                  { label: "Amount", value: transactionDetails.amount ? `AFA ${transactionDetails.amount}` : "-" },
                ];
                // Render all fields
                return [
                  ...commonFields.map(f => renderField(f.label, f.value)),
                  ...(transactionDetails.transaction_category === 'eTop Up'
                    ? eTopUpFields.map(f =>
                        renderField(
                          f.label,
                          f.value !== undefined
                            ? f.value
                            : (transactionDetails as any)[f.key] ?? "-"
                        )
                      )
                    : otherFields.map(f =>
                        renderField(
                          f.label,
                          f.value !== undefined
                            ? f.value
                            : (transactionDetails as any)[f.key] ?? "-"
                        )
                      )
                  ),
                ];
              })()}
            </div>
            <div className="mt-6">
              <h1 className="text-body2 w-full text-primary_text">
                Reason for Reversal
              </h1>
              <p className="text-body1 break-all">
                {transactionDetails.flagged_reason || "-"}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <Shimmer />
      )}
      {isModalOpen && (
        <CustomModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          title={
            isApprovalModal ? "Approve Reversal Request" : "Confirm Rejection"
          }
          subText={`Are you sure you want to ${isApprovalModal ? "approve" : "reject"
            } the reversal request?`}
          primaryButtonText={isApprovalModal ? "Accept" : "Reject"}
          secondaryButtonText={translate("Cancel")}
          secondaryButton={true}
          handelSubmit={handleApproval}
          buttonColor={isApprovalModal ? "bg-positive" : "bg-negative"}
          buttonLoading={buttonLoading}
          buttonWidth={"w-[95px]"}
          showCloseButton={true}
          secondaryWidth={"text-large"}
          children={
            <>
              {isApprovalModal ? (
                <>
                  <CurrencyInputField
                    inputItem={{
                      labelName: "Amount",
                      id: "amount",
                    }}
                    placeholder={"Enter amount"}
                    errorMessage={reasonInputError}
                    value={reasonInput}
                    required={true}
                    disabled={transactionDetails?.payment_type === "advance"}
                    tabIndex={0}
                    testId={"received-amount"}
                    maxLength={15}
                    onChange={(e) => {
                      handleAmountChange(e);
                    }}
                    maindivstyle={`w-full mt-2 ${calculatedCommissionAmount !== null ? 'mb-2' : 'mb-[32px]'}`}
                    className={"w-full"}
                    customWidth={"w-full"}
                  />
                  {calculatedCommissionAmount !== null && transactionDetails?.commission_details?.commission_type && transactionDetails?.commission_details?.commission_percentage && (
                    <div className="mb-[32px] p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-body2 text-primary_text">
                        <span className="font-semibold">Note:</span> This transaction includes a{" "}
                        <span className="font-semibold">{transactionDetails?.commission_details?.commission_percentage}% {transactionDetails?.commission_details?.commission_type}</span> commission.
                        <br />
                        <span className="mt-1 block">Commission amount to be reversed:{" "}
                        <span className="font-semibold text-blue-700">AFA {calculatedCommissionAmount.toFixed(2)}</span></span>
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <TextArea
                  label="Reason"
                  required={true}
                  placeholderRequied={false}
                  text={reasonInput}
                  setText={setReasonInput}
                  textError={reasonInputError}
                  setTextError={setReasonInputError}
                  bgColor={"bg-white"}
                  maxLength={250}
                  id={`reason-note`}
                  height={"h-[140px]"}
                />
              )}
            </>
          }
        />
      )}
    </div>
  );
}
